import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import maplibregl, { Map as MLMap, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api } from "../lib/api";

const STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

export function FacilityNetworkMap({ edrpou }: { edrpou: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["facility-network", edrpou],
    queryFn: () => api.get<any>(`/api/v1/facilities/${edrpou}/divisions.geojson`),
    enabled: !!edrpou,
  });

  useEffect(() => {
    console.warn("[FacilityNetworkMap] useEffect fired, ref =", containerRef.current);
    if (!containerRef.current) {
      console.warn("[FacilityNetworkMap] container ref is null, skipping init");
      return;
    }
    if (mapRef.current) {
      console.warn("[FacilityNetworkMap] map already exists, skipping");
      return;
    }
    const w = containerRef.current.offsetWidth;
    const h = containerRef.current.offsetHeight;
    console.warn(`[FacilityNetworkMap] init for ${edrpou}, container ${w}x${h}`);
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: [31.0, 49.0],
      zoom: 5,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.on("load", () => console.warn("[FacilityNetworkMap] style loaded"));
    map.on("error", (e) => console.error("[FacilityNetworkMap] map ERROR:", e));
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);
    // Множинні resize щоб зловити layout
    const ts = [50, 200, 500, 1000].map((d) => setTimeout(() => map.resize(), d));
    return () => {
      ro.disconnect();
      ts.forEach(clearTimeout);
      map.remove();
      mapRef.current = null;
    };
  }, [edrpou]);

  useEffect(() => {
    if (!data || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      // Розділяємо features на main / division / link
      const main = data.features.find((f: any) => f.properties.kind === "main");
      const divisions = {
        type: "FeatureCollection",
        features: data.features.filter((f: any) => f.properties.kind === "division"),
      };
      const links = {
        type: "FeatureCollection",
        features: data.features.filter((f: any) => f.properties.kind === "link"),
      };

      const upsert = (id: string, geo: any) => {
        const src = m.getSource(id) as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData(geo);
        else m.addSource(id, { type: "geojson", data: geo });
      };
      upsert("main", { type: "FeatureCollection", features: main ? [main] : [] });
      upsert("divisions", divisions);
      upsert("links", links);

      if (!m.getLayer("links-line")) {
        m.addLayer({
          id: "links-line", type: "line", source: "links",
          paint: { "line-color": "#dd6b20", "line-width": 1.5, "line-opacity": 0.5, "line-dasharray": [2, 1] },
        });
      }
      if (!m.getLayer("divisions-circle")) {
        m.addLayer({
          id: "divisions-circle", type: "circle", source: "divisions",
          paint: {
            "circle-radius": 4, "circle-color": "#dd6b20",
            "circle-stroke-width": 1, "circle-stroke-color": "#fff",
          },
        });
        m.on("click", "divisions-circle", (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const [lng, lat] = (f.geometry as any).coordinates;
          const p = f.properties as any;
          new Popup({ closeButton: true, maxWidth: "260px" })
            .setLngLat([lng, lat]).setHTML(`
              <div style="font-size:12px;line-height:1.4;max-width:240px">
                <div style="font-weight:600;color:#1a365d">${escape(p.name || "—")}</div>
                <div style="color:#718096;font-size:10px">${p.type || ""}</div>
                ${p.address ? `<div style="color:#4a5568;font-size:11px;margin-top:4px">📍 ${escape(p.address)}</div>` : ""}
                ${p.phone ? `<div style="color:#4a5568;font-size:11px">☎ ${p.phone}</div>` : ""}
              </div>`).addTo(m);
        });
        m.on("mouseenter", "divisions-circle", () => (m.getCanvas().style.cursor = "pointer"));
        m.on("mouseleave", "divisions-circle", () => (m.getCanvas().style.cursor = ""));
      }
      if (!m.getLayer("main-circle")) {
        m.addLayer({
          id: "main-circle", type: "circle", source: "main",
          paint: {
            "circle-radius": 8, "circle-color": "#2c5282",
            "circle-stroke-width": 2, "circle-stroke-color": "#fff",
          },
        });
      }

      // Fit bounds
      const coords = data.features
        .filter((f: any) => f.geometry.type === "Point")
        .map((f: any) => f.geometry.coordinates);
      if (coords.length > 0) {
        if (coords.length === 1) {
          m.flyTo({ center: coords[0], zoom: 13, duration: 800 });
        } else {
          const lngs = coords.map((c: any) => c[0]);
          const lats = coords.map((c: any) => c[1]);
          m.fitBounds(
            [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
            { padding: 40, maxZoom: 12, duration: 800 },
          );
        }
      }
    };
    const run = () => {
      m.resize();
      apply();
    };
    if (m.isStyleLoaded()) run();
    else m.once("load", run);
  }, [data]);

  const divCount = data?._count_divisions || 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, fontSize: 12, color: "#4a5568" }}>
        <div>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#2c5282", marginRight: 6, verticalAlign: "middle" }} />
          <strong>Головний заклад</strong>
          {data && (
            <>
              {" · "}
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#dd6b20", margin: "0 6px 0 8px", verticalAlign: "middle" }} />
              <strong>{divCount}</strong> підрозділ{divCount === 1 ? "" : (divCount < 5 ? "и" : "ів")}
            </>
          )}
        </div>
        <div style={{ fontSize: 11, color: "#a0aec0" }}>клік на маркер → деталі</div>
      </div>
      <div style={{ position: "relative" }}>
        <div ref={containerRef} style={{
          width: "100%", height: "400px", minHeight: "400px",
          border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden",
          background: "#f0f4f8",
        }} />
        {isLoading && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            background: "rgba(247,250,252,0.9)", borderRadius: 6,
            color: "#718096", fontSize: 13, pointerEvents: "none",
          }}>Завантаження мережі…</div>
        )}
        {error && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            background: "rgba(255,245,245,0.95)", borderRadius: 6,
            color: "#c53030", fontSize: 12, padding: 12,
          }}>Помилка: {(error as any)?.detail}</div>
        )}
      </div>
      <div style={{
        marginTop: 10, padding: 8, background: "#f0fff4",
        border: "1px solid #9ae6b4", borderRadius: 4, fontSize: 11, color: "#22543d",
      }}>
        ✓ Цей заклад — <strong>самостійна юридична особа</strong> з власним договором НСЗУ.
        Підрозділи — фізичні точки надання послуг (амбулаторії, ФАПи, філії), що належать цій юр.особі.
      </div>
    </div>
  );
}

function escape(s: string): string {
  return (s || "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c)
  );
}
